import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useFlags } from "@/lib/flags";
import { usd, plural } from "@/components/common";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Users, CirclePlus, UserPlus, Minus, Plus, Loader2 } from "lucide-react";

// Seats and limits (FEATURE_WALLET; backend/seats.py; design_handoff_team_roles/ screens 1, 2 and 11): a plan's team
// member seats and independent professional slots, the extras at $10 a month ($9 of it AI credits), and investors,
// who are free. Shown with a live plan (GET /seats `available`). The founder adds an extra by inviting someone beyond the
// plan (the Store card's buttons open the Invite dialog with that title); a cancelled plan (`can_add` false) takes none.

export function useSeats() {
  const on = useFlags().wallet;
  const { data } = useQuery({ queryKey: ["seats"], enabled: !!on, queryFn: async () => (await api.get("/seats")).data });
  return on && data?.available ? data : null;
}

const money = (n) => usd(n, n % 1 ? 2 : 0);

// A subscription bought before seats keeps its older price, where extras can't be charged (backend/seats.py billable)
const OLD_PRICE = "Your plan is still on its price from before seats, so extras aren't charged, and add no bonus, until it moves to the new price. Changing your plan moves it.";
// the $9 of each $10 extra that goes into the wallet is a bonus in the app and on /pricing (owner, 2026-10-16)
const bonus = (n) => `a ${money(n)} AI credit bonus`;

// Why a plan that isn't running takes no extras, and what to do (backend/seats.py held())
const held = (s) => s.status === "cancelled" ? ["it's cancelled", "Keep your plan in Store"] : ["it isn't running right now", "Pay or resume it in Store"];

// "6 of 5 team member seats (1 extra, $10 a month)"
const count = (k, label, s) => `${k.in_use} of ${k.included} ${label}${k.extra ? ` (${k.extra} extra, ${money(k.extra * s.price_usd)} a month)` : ""}`;

// The Team page's line under the header
export function SeatsLine({ s }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 rounded-[10px] bg-card text-sm" data-testid="team-seats">
      <Users className="w-3.5 h-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span>{s.plan} plan: {count(s.employee, "team member seats", s)}, {count(s.independent, "independent professionals", s)}.</span>
      <span className="text-muted-foreground">Investors are free and don't use a seat.</span>
      <Link to="/founder/store#seats" className="ms-auto underline underline-offset-2 hover:opacity-50">Seats and limits</Link>
    </div>
  );
}

// Does one more of this kind add an extra? (the Invite dialog, a new title)
export const addsExtra = (s, kind) => !!s && s[kind].in_use + 1 > s[kind].included + s[kind].free;

// The Invite dialog's plan line, when the invite adds an extra seat or slot
export function PlanLine({ s, kind }) {
  const k = s[kind];
  if (!s.can_add) return (  // a cancelled plan keeps its limits and takes no new extras (backend/seats.py)
    <div className="flex flex-col gap-1.5 p-3.5 rounded-lg border border-gr-warning text-sm" data-testid="invite-plan-line">
      <span>Your {s.plan} plan includes {k.included} {kind === "employee" ? "team member seats" : "independent professionals"}, and {held(s)[0]}, so it can't take an extra.</span>
      <span className="flex flex-wrap gap-x-4 gap-y-1">
        <Link to="/founder/store" className="underline underline-offset-2 hover:opacity-50">{held(s)[1]}</Link>
        <Link to="/founder/team" className="underline underline-offset-2 hover:opacity-50">Finish and remove someone</Link>
      </span>
    </div>
  );
  return (
    <div className="flex flex-col gap-1.5 p-3.5 rounded-lg border text-sm" data-testid="invite-plan-line">
      <span className="flex items-start gap-2"><CirclePlus className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
        {kind === "employee"
          ? `Your ${s.plan} plan includes ${k.included} team member seats. This one adds ${money(s.price_usd)} a month, with ${bonus(s.credits_usd)}.`
          : `Your ${s.plan} plan includes ${k.included} independent professionals. This one adds an extra slot at ${money(s.price_usd)} a month, with ${bonus(s.credits_usd)}.`}
      </span>
      {s.billed === false && <span className="ps-[22px] text-muted-foreground">{OLD_PRICE}</span>}
      {kind === "independent" && (
        <span className="flex flex-wrap gap-x-4 gap-y-1 ps-[22px]">
          <Link to="/founder/team" className="underline underline-offset-2 hover:opacity-50">Finish and remove someone whose task is done</Link>
          <Link to="/founder/store" className="underline underline-offset-2 hover:opacity-50">Move up a plan</Link>
        </span>
      )}
    </div>
  );
}

// Store -> Seats and limits: extras bought ahead, per kind (x1, x2, x3...; PUT /seats/bought, backend/seats.py buy).
// The counter shows each kind's extras now, so + is one more; it can't go below the extras in use. Bought is sent only
// above those (back to them, extras follow the team again).
const KINDS = [["employee", "Extra team member seats"], ["independent", "Extra independent professional slots"]];

function BuySeats({ s }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState({ employee: s.employee.extra, independent: s.independent.extra });
  const [asking, setAsking] = useState(false);
  useEffect(() => { setDraft({ employee: s.employee.extra, independent: s.independent.extra }); }, [s.employee.extra, s.independent.extra]);
  // extras in use beyond the plan (the rest of a kind's extras are bought and not used yet)
  const used = (k) => s[k].extra - (s[k].spare || 0);
  const send = (k) => (draft[k] > used(k) ? draft[k] : 0);
  const save = useMutation({
    mutationFn: async () => (await api.put("/seats/bought", { employee: send("employee"), independent: send("independent") })).data,
    onSuccess: (d) => {
      qc.setQueryData(["seats"], d); qc.invalidateQueries({ queryKey: ["seats"] });
      toast.success("Extra seats saved. Your next invoice shows the change.");
      setAsking(false);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const more = KINDS.reduce((n, [k]) => n + Math.max(0, draft[k] - s[k].extra), 0);
  const fewer = KINDS.reduce((n, [k]) => n + Math.max(0, s[k].extra - draft[k]), 0);
  const changed = more + fewer > 0;
  const step = (k, d) => setDraft({ ...draft, [k]: Math.max(used(k), Math.min(used(k) + 100, draft[k] + d)) });
  return (
    <div className="flex flex-col gap-2.5 pt-3 border-t" data-testid="store-seats-buy">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-semibold">Buy extras ahead</span>
        <span className="text-sm text-gr-ink-secondary [text-wrap:pretty]">For people you'll invite soon. Each is {money(s.price_usd)} a month with {bonus(s.credits_usd)}, and stays until you lower it.</span>
      </div>
      {KINDS.map(([k, label]) => (
        <div key={k} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
          <span className="min-w-[200px] flex-1">{label}</span>
          <span className="flex items-center gap-2">
            <Button type="button" variant="outline" size="icon" className="h-8 w-8" aria-label={`One fewer ${label.toLowerCase()} bought`}
              disabled={draft[k] <= used(k)} onClick={() => step(k, -1)} data-testid={`seats-buy-${k}-minus`}><Minus /></Button>
            <span className="w-8 text-center tabular-nums font-semibold" aria-live="polite" data-testid={`seats-buy-${k}`}>{draft[k]}</span>
            <Button type="button" variant="outline" size="icon" className="h-8 w-8" aria-label={`One more ${label.toLowerCase()} bought`}
              onClick={() => step(k, 1)} data-testid={`seats-buy-${k}-plus`}><Plus /></Button>
          </span>
          <span className="text-muted-foreground tabular-nums w-[150px] text-end">
            {[used(k) && `${used(k)} in use`, draft[k] > used(k) && `${draft[k] - used(k)} not used yet`].filter(Boolean).join(" · ") || "None yet"}
          </span>
        </div>
      ))}
      <div className="flex flex-wrap items-center justify-end gap-3">
        {changed && <span className="text-sm text-muted-foreground">
          {[more && `${plural(more, "more extra", "more extras")}: +${money(more * s.price_usd)} a month`,
            fewer && `${plural(fewer, "fewer extra", "fewer extras")} from your next renewal`].filter(Boolean).join(" · ")}
        </span>}
        <Button type="button" disabled={!changed || save.isPending} onClick={() => setAsking(true)} data-testid="seats-buy-save">
          {more ? `Buy ${plural(more, "extra", "extras")}` : "Save"}
        </Button>
      </div>
      {asking && (
        <AlertDialog open onOpenChange={(v) => !v && setAsking(false)}>
          <AlertDialogContent className="gap-3.5">
            <AlertDialogTitle>{more ? `Buy ${plural(more, "extra", "extras")}?` : `Lower your extras by ${fewer}?`}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="flex flex-col gap-2 text-sm text-gr-ink-secondary">
                {more > 0 && <p className="m-0 [text-wrap:pretty]">{money(more * s.price_usd)} a month more on your {s.plan} plan, with {bonus(more * s.credits_usd)} in your wallet once each invoice is paid. Added now, it's charged prorated on your next invoice.</p>}
                {fewer > 0 && <p className="m-0 [text-wrap:pretty]">{plural(fewer, "extra", "extras")} fewer from your next renewal. A bonus already given stays in your wallet.</p>}
              </div>
            </AlertDialogDescription>
            <AlertDialogFooter className="pt-1">
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => { e.preventDefault(); save.mutate(); }} disabled={save.isPending} data-testid="seats-buy-confirm">
                {save.isPending && <Loader2 className="animate-spin" />}{more ? `Buy for ${money(more * s.price_usd)} a month` : "Lower it"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

// The Store's Seats and limits card
export function SeatsCard({ s }) {
  const row = (label, k, investors) => (
    <div key={label} className="grid grid-cols-[minmax(0,1.6fr)_repeat(4,minmax(0,1fr))] gap-3 py-2 border-t">
      <span>{label}</span>
      <span className="text-end">{investors ? s.investors : k.in_use}</span>
      <span className="text-end">{investors ? "Unlimited" : k.included}</span>
      <span className="text-end">{investors ? "—" : k.extra || "—"}</span>
      <span className="text-end">{investors ? "Free" : k.extra ? `${money(k.extra * s.price_usd)} · ${money(k.extra * s.credits_usd)} bonus` : "—"}</span>
    </div>
  );
  const free = s.employee.free + s.independent.free;
  return (
    <section id="seats" aria-label="Seats and limits" className="bg-card rounded-[10px] p-[18px] flex flex-col gap-3 scroll-mt-4" data-testid="store-seats">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="font-display text-[15px] tracking-[0.02em] font-semibold">Seats and limits</span>
        <Link to="/founder/team" className="text-sm underline underline-offset-2 hover:opacity-50">See your team</Link>
      </div>
      <p className="m-0 text-sm text-gr-ink-secondary [text-wrap:pretty]">
        {s.plan} plan: {s.employee.included} team member seats included{s.employee.extra ? `, ${s.employee.extra} extra at ${money(s.price_usd)} (${bonus(s.credits_usd)} each)` : ""}.{" "}
        {s.independent.included} independent professionals included{s.independent.extra ? `, ${plural(s.independent.extra, "extra slot")} at ${money(s.price_usd)}` : ""}.
      </p>
      <div className="overflow-x-auto">
        <div className="min-w-[520px] text-sm tabular-nums">
          <div className="grid grid-cols-[minmax(0,1.6fr)_repeat(4,minmax(0,1fr))] gap-3 pb-1.5 text-xs uppercase tracking-[0.09em] text-muted-foreground">
            <span /><span className="text-end">In use</span><span className="text-end">Included</span><span className="text-end">Extra</span><span className="text-end">A month</span>
          </div>
          {row("Team member seats", s.employee)}
          {row("Independent professionals", s.independent)}
          {row("Investors", null, true)}
        </div>
      </div>
      {s.can_add && s.billed !== false && <BuySeats s={s} />}
      {s.can_add ? (
        <div className="flex flex-col gap-2" data-testid="store-seats-add">
          <p className="m-0 text-sm text-gr-ink-secondary [text-wrap:pretty]">
            To add an extra seat or slot, invite the person who'll use it. The Invite dialog shows what it adds before you send.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline"><Link to="/founder/team?invite=employee"><UserPlus />Add a team member</Link></Button>
            <Button asChild variant="outline"><Link to="/founder/team?invite=independent"><UserPlus />Add an independent professional</Link></Button>
          </div>
        </div>
      ) : (
        <p className="m-0 text-sm text-gr-ink-secondary [text-wrap:pretty]" data-testid="store-seats-add">
          Your {s.plan} plan includes these, and {held(s)[0]}, so it can't take extra seats or slots. {s.status === "cancelled" ? "Keep your plan" : "Pay or resume it"} above to add one.
        </p>
      )}
      {s.billed === false && <p className="m-0 text-sm text-gr-warning [text-wrap:pretty]" data-testid="store-seats-old-price">{OLD_PRICE}</p>}
      <p className="m-0 text-sm text-muted-foreground [text-wrap:pretty]">
        Each extra is {money(s.price_usd)} a month and comes with {bonus(s.credits_usd)} in your wallet. Added mid-month, it's charged prorated on your next invoice. Fewer extras apply from your next renewal; a bonus already given stays in the wallet.
        {free > 0 && ` ${plural(free, "person", "people")} from before seats stay free until they leave.`}
      </p>
    </section>
  );
}
